import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import dayjs from "dayjs";
import TabsHT from "../../components/tabs-ht/TabsHT";
import { usePropertyStore } from "../../contexts/PropertyStoreContext";
import { processedDateARIData } from "../../constants/ARI-utils";
import { useAriContext } from "../../contexts/AriContext";
import { OFFLINE_CRS_ID } from "../../constants";
import TabsFilter from "./TabsFilter";
import SoldOutBadge from "./SoldOutBadge";
import Loader from "../../components/Loader";
import {
  selectLoading,
  selectRoomtypes,
  selectSelectedRoomtype,
  setSelectedRoomtype,
} from "../../slices/roomtypeSlice";
import "./roomtypeTabs.scss";

const RoomtypeTabs = ({
  rateplanId,
  startDate,
  endDate,
  render,
  onTabSelect,
  includeNumberOfRoomsSold = false,
  onConfirmTabChange,
}) => {
  const [filteredRooms, setFilteredRooms] = useState([]);
  const { selectedProperty } = usePropertyStore();
  const {
    ariData,
    refetchAriData,
    loading,
    backgroundLoading,
    loadedRoomtypeIds,
    prioritizeRoomtype,
  } = useAriContext();
  const dispatch = useDispatch();
  const rooms = useSelector(selectRoomtypes);
  const loadingRooms = useSelector(selectLoading);
  const selectedRoomtype = useSelector(selectSelectedRoomtype);

  const isOffline = selectedProperty?.crsId === OFFLINE_CRS_ID;

  // Determine which rooms to display: filtered rooms if any, otherwise all rooms
  const roomsList = useMemo(() => {
    const result = filteredRooms?.length > 0 ? filteredRooms : rooms;
    return result;
  }, [filteredRooms, rooms]);

  // Create a stable dependency based on room IDs to prevent duplicate API calls
  const roomIds = useMemo(
    () => roomsList.map((room) => room.id).join(","),
    [roomsList]
  );

  // Ref so the fetch effect can prioritize the current tab without tab
  // switches re-triggering a refetch (selectedRoomtype stays out of its deps).
  const selectedRoomtypeRef = useRef(selectedRoomtype);
  selectedRoomtypeRef.current = selectedRoomtype;

  useEffect(() => {
    refetchAriData({
      propertyId: selectedProperty?.propertyId,
      rateplanId,
      rooms,
      startDate,
      endDate,
      includeNumberOfRoomsSold,
      priorityRoomtypeId: selectedRoomtypeRef.current,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rateplanId,
    roomIds, // Use stable roomIds instead of rooms array to prevent duplicate calls
    startDate,
    endDate,
    selectedProperty?.propertyId,
    refetchAriData,
    includeNumberOfRoomsSold,
  ]);

  const handleRoomChange = (index) => {
    // Validate index is within bounds
    if (index < 0 || index >= roomsList.length) {
      return Promise.resolve();
    }

    const roomId = roomsList[index].id;

    // If this tab's batch hasn't landed yet, move it to the front of the
    // fetch queue so the user waits the least possible time.
    if (!loadedRoomtypeIds?.has(roomId)) {
      prioritizeRoomtype?.(roomId);
    }

    return new Promise((resolve) => {
      if (onConfirmTabChange) {
        onConfirmTabChange(() => {
          dispatch(setSelectedRoomtype(roomId));
          resolve();
        });
      } else {
        dispatch(setSelectedRoomtype(roomId));
        resolve();
      }
      if (onTabSelect) {
        onTabSelect();
      }
    });
  };

  // Memoize tabsData calculation to prevent excessive re-renders
  const tabsData = useMemo(() => {
    const tabsDataMap = {};
    roomsList.forEach(({ id, maxAdultOccupancy }) => {
      tabsDataMap[id] = ariData
        ?.filter(({ roomtypeId }) => roomtypeId === id)
        .map((ari) => processedDateARIData(ari, maxAdultOccupancy));
    });
    return tabsDataMap;
  }, [roomsList, ariData]);

  // Handle active room selection when filter changes
  useEffect(() => {
    if (roomsList.length === 0) {
      return;
    }

    const shouldSelectFirstRoom =
      !selectedRoomtype ||
      !roomsList.some((room) => room.id === selectedRoomtype);

    if (shouldSelectFirstRoom) {
      dispatch(setSelectedRoomtype(roomsList[0].id));
    }
  }, [roomsList, selectedRoomtype, dispatch]);

  if (loadingRooms) {
    return <Loader loading={loadingRooms} />;
  }

  const initiallyActiveTab = Math.max(
    0,
    roomsList.findIndex(({ id }) => id === selectedRoomtype)
  );

  // Block the view for the foreground fetch, or when the selected tab's own
  // batch is still loading in the background; other background batches load
  // silently (sold-out badges fill in as they land).
  const showAriLoader =
    loading ||
    (backgroundLoading &&
      !!selectedRoomtype &&
      !loadedRoomtypeIds?.has(selectedRoomtype));

  return (
    <>
      {showAriLoader && <Loader loading={showAriLoader} />}
      <TabsHT
        initiallyActiveTab={initiallyActiveTab}
        className={classNames("tab-content", { "with-tag-picker": !isOffline })}
      >
        <></>
        <TabsHT.TabList>
          {roomsList.map(({ id, displayName }) => {
            const currentTabData = ariData?.filter(
              ({ roomtypeId }) => roomtypeId === id
            );
            const isSoldOut = currentTabData?.some(
              ({ inventory, date, isOpen }) =>
                !inventory && isOpen && !dayjs(date).isBefore(dayjs(), "day")
            );
            return (
              <TabsHT.Tab onClick={handleRoomChange} key={id}>
                <SoldOutBadge
                  isSoldOut={isSoldOut}
                  className={"tab-soldOut-icon"}
                />
                {displayName}
              </TabsHT.Tab>
            );
          })}
        </TabsHT.TabList>
      </TabsHT>
      {selectedRoomtype ? render(tabsData[selectedRoomtype]) : null}
      {!isOffline && (
        <TabsFilter {...{ rooms, ariData, filteredRooms, setFilteredRooms }} />
      )}
    </>
  );
};

RoomtypeTabs.propTypes = {
  rateplanId: PropTypes.number,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  render: PropTypes.func.isRequired,
  onTabSelect: PropTypes.func,
  includeNumberOfRoomsSold: PropTypes.bool,
  onConfirmTabChange: PropTypes.func,
};

export default RoomtypeTabs;
